import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
  inject,
  input,
} from '@angular/core';

const BUTTON_HOST_ATTRIBUTES: string[] = ['ui-admin-button'];

const BUTTON_STYLE_CLASSES: { [name: string]: { [name: string]: string } } = {
  'ui-admin-button': {
    default: '',
    primary:
      'disabled:opacity-70 font-semibold bg-primary text-white rounded-lg items-center gap-2',
    'purple-95':
      'disabled:opacity-70 font-semibold bg-purple-95 text-primary rounded-lg items-center gap-2',
    'neutral-90':
      'disabled:opacity-70 font-semibold bg-neutral-90 text-neutral-50 rounded-lg items-center gap-2',
    accent:
      'disabled:bg-secondary-borders disabled:text-secondary-muted bg-accent-base rounded-lg text-white',
    'accent-outlined':
      'disabled:border-secondary-borders disabled:text-secondary-muted border border-accent-base hover:bg-accent-base hover:text-white',
    danger:
      'disabled:bg-secondary-borders disabled:text-secondary-muted bg-[rgb(255,0,0,0.8)] text-white',
    'danger-outlined':
      'disabled:border-secondary-borders disabled:text-secondary-muted hover:bg-[rgb(255,0,0,0.8)] hover:text-white border border-[rgb(255,0,0,0.8)] hover:transition-[width] duration-300 ease-in-out',
  },
};

@Component({
  imports: [],
  templateUrl: './admin-button.component.html',
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'button[ui-admin-button],a[ui-admin-button]',
  exportAs: 'uiAdminButton',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminButtonComponent implements AfterViewInit, OnChanges {
  private _elementRef = inject(ElementRef);

  @Input() color = '';
  readonly disabled = input(false);
  readonly isFull = input(false);
  readonly isLarge = input(false);
  readonly isIcon = input(false);
  @HostBinding('attr.disabled') get valid() {
    return this.disabled() ? 'disabled' : null;
  }
  private classes = {
    button: ['py-[10px]', 'px-5'],
    icon: ['w-10', 'h-10'],
  };

  ngOnChanges(changes: SimpleChanges): void {
    // Check if the 'color' input has changed
    if (changes['color']) {
      for (const variant of BUTTON_HOST_ATTRIBUTES) {
        if (this.hasHostAttributes(variant)) {
          if (
            BUTTON_STYLE_CLASSES[variant] &&
            BUTTON_STYLE_CLASSES[variant][changes['color'].previousValue]
          ) {
            const allPossibleClasses: string[] = [];
            BUTTON_STYLE_CLASSES[variant][changes['color'].previousValue]
              .split(' ')
              .forEach((className) => {
                if (allPossibleClasses.indexOf(className) === -1) {
                  // Check for uniqueness
                  allPossibleClasses.push(className);
                }
              });
            allPossibleClasses.forEach((className) => {
              this.getHostElement().classList.remove(className);
            });
          }
          if (
            BUTTON_STYLE_CLASSES[variant] &&
            BUTTON_STYLE_CLASSES[variant][this.color]
          ) {
            this.addToClassList(BUTTON_STYLE_CLASSES[variant][this.color]);
          }
        }
      }

      if (this.isFull()) {
        this.addToClassList('w-full');
      }
    }
  }

  ngAfterViewInit(): void {
    // base style for button
    this.getHostElement().classList.add(
      'inline-flex',
      'justify-center',
      'items-center',
      'rounded-[4px]',
      ...(this.isIcon() ? this.classes.icon : this.classes.button)
    );

    const padding = this.isLarge() ? 'font-normal' : 'font-normal';
    this.addToClassList(padding);
    this.color = this.color || 'default';

    // For each of the attributes, add text and background classes corresponding
    // to each variant
    for (const variant of BUTTON_HOST_ATTRIBUTES) {
      if (this.hasHostAttributes(variant)) {
        if (
          BUTTON_STYLE_CLASSES[variant] &&
          BUTTON_STYLE_CLASSES[variant][this.color]
        ) {
          this.addToClassList(BUTTON_STYLE_CLASSES[variant][this.color]);
        }
      }
    }

    if (this.isFull()) {
      this.addToClassList('w-full');
    }
  }

  private addToClassList(className: string) {
    const classes = className.split(' ');
    this.getHostElement().classList.add(...classes);
  }
  private removeClassList(classList: string) {
    this.getHostElement().classList.remove(classList);
  }

  private hasHostAttributes(...attributes: string[]) {
    return attributes.some((attribute) =>
      this.getHostElement().hasAttribute(attribute)
    );
  }

  private getHostElement(): HTMLElement {
    return this._elementRef.nativeElement;
  }
}
